import React, {
  FC, useMemo, useState, useEffect,
} from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import RenderContent from 'app/components/layout/RenderContent';
import InputGroupBlock from 'app/components/general/InputGroupBlock';
import SectionGroup from 'app/components/general/SectionGroup';
import HeaderThin from 'app/components/layout/HeaderThin';
import { IApplicationState } from 'app/types/state';
import { bindActionCreators } from 'redux';

import _ from 'lodash';

import * as teamActions from 'app/ducks/teams/actions';
import * as teamMemberActions from 'app/ducks/teamMembers/actions';
import * as paymentActions from 'app/ducks/payments/actions';
import * as paymentCollectionActions from 'app/ducks/paymentCollections/actions';
import * as reminderActions from 'app/ducks/reminders/actions';
import * as dashboardActions from 'app/ducks/dashboard/actions';

import * as loadingSelectors from 'app/ducks/loading/selectors';
import * as teamSelectors from 'app/ducks/teams/selectors';
import * as userSelectors from 'app/ducks/users/selectors';
import * as teamMemberSelectors from 'app/ducks/teamMembers/selectors';
import * as paymentSelectors from 'app/ducks/payments/selectors';
import * as paymentCollectionSelectors from 'app/ducks/paymentCollections/selectors';
import * as reminderSelectors from 'app/ducks/reminders/selectors';
import * as seasonsActions from 'app/ducks/seasons/actions';
import * as seasonsSelectors from 'app/ducks/seasons/selectors';
import DashboardUpcomingMatches from '../DashboardUpcomingMatches';
import DashboardSeasonOverview from '../DashboardSeasonOverview';
import DashboardPaymentRequestSummary from '../DashboardPaymentRequestSummary';
import DashboardActivePaymentRequests from '../DashboardActivePaymentRequests';

interface IProps {
  teamId: string;
  seasonId?: string;
  actions: any;
  users: any;
  teamMembers: any;
  payments: any;
  teams: any;
  seasons: any;
  paymentCollections: any;
  organisations: any;
  reminders: any;
  match: any;
  dashboard: any;
  appReady: boolean;
}

interface IState {
  selectedTeam: any;
}

const selector = (state): any => ({
  teams: {
    ...teamSelectors.getTeams(state),
    getPaymentIds: (teamId) => teamSelectors.getPaymentIds(state, teamId),
    getTeamMemberIds: (teamId) => teamSelectors.getTeamMemberIds(state, teamId),
    getCurrentTeam: () => teamSelectors.getCurrentTeam(state),
  },
  teamMembers: teamMemberSelectors.getTeamMembers(state),
  users: userSelectors.getUsers(state),
  payments: paymentSelectors.getPayments(state),
  paymentCollections: paymentCollectionSelectors.getPaymentCollections(state),
  reminders: reminderSelectors.getReminders(state),
  seasons: seasonsSelectors.getSeasons(state),
  appReady: loadingSelectors.appReady(state),
});

const actionCreators = _.merge(
  teamActions,
  teamMemberActions,
  paymentActions,
  paymentCollectionActions,
  reminderActions,
  seasonsActions,
  dashboardActions,
);

const DashboardAllTeams: FC<IProps> = (props: IProps = {
  actions: undefined,
  users: undefined,
  teamMembers: undefined,
  payments: undefined,
  seasons: undefined,
  teams: undefined,
  paymentCollections: undefined,
  organisations: undefined,
  reminders: undefined,
  match: undefined,
  dashboard: undefined,
  appReady: false,
}) => {
  const [filterStatus, setFilterStatus] = useState('pending');

  const selectedSeason = props.dashboard.seasonSelection;

  const handleSelectSeason = (e: any): void => {
    props.actions.updateSeasonSelection(e);
  };

  const seasonOptions = _.filter(props.seasons?.seasonsByIdInSelectFormat, (s) => s.name !== null);

  const groupIds = useMemo(() => {
    const arrGroupIds = [];
    _.each(props.teams.teamsById, (team) => {
      _.each(team.groups, (g) => {
        if (!_.isEmpty(g?.id)) {
          arrGroupIds.push(g?.id);
        }
      });
    });
    return arrGroupIds;
  }, [JSON.stringify(props.teams.teamsById)]);

  const orgIds = useMemo(() => {
    const arrOrgIds = [];
    _.each(props.teams.teamsById, (team) => {
      _.each(team.organisations, (o) => {
        if (!_.isEmpty(o?.id)) {
          arrOrgIds.push(o?.id);
        }
      });
    });
    return arrOrgIds;
  }, [JSON.stringify(props.teams.teamsById)]);

  useEffect(() => {
    if (props.seasonId !== undefined) {
      props.actions.selectSeason(props.seasonId);
    } else if (props.seasons.currentSeasonId === undefined && !_.isEmpty(props.seasons.seasonIds)) {
      props.actions.selectSeason(props.seasons.seasonIds[0]);
      props.actions.updateSeasonSelection({
        id: props.seasons.seasonsById[props.seasons.seasonIds[0]].id,
        name: props.seasons.seasonsById[props.seasons.seasonIds[0]].name,
      });
    }
  }, [JSON.stringify(props.seasons.seasonsById), props.seasonId]);

  return (
    <div>
      <div className="row">
        <div className="col">
          <DashboardUpcomingMatches {...props} multipleTeams groupIds={groupIds} orgIds={orgIds} />
        </div>
      </div>
      <div className={classnames('row')}>
        <div className="col">
          <DashboardPaymentRequestSummary {...props} setFilterStatus={setFilterStatus} />
        </div>
      </div>
      <div className={classnames('row with-margin-top-extra', { 'd-none': _.isEmpty(props.seasons.currentSeason) })}>
        <div className="col">
          <div className="row section-title">
            <div className="col col-12 section-title-col col-lg-6">
              <h3>Season Overview</h3>
            </div>
            <div className="col col-12 margin-top-10-md section-title-col col-lg-6 btn-flex-group">
              <div className="btn-flex-container">
                {!_.isEmpty(seasonOptions) && (
                  <InputGroupBlock
                    name="season"
                    // labelText="Time Period"
                    className="no-margin-bottom float-right w-100"
                    inputGroupClassName="no-margin-bottom w-100"
                    placeholder="Season"
                    type="select"
                    options={seasonOptions}
                    value={selectedSeason}
                    onChange={handleSelectSeason}
                  />
                )}
              </div>

            </div>
          </div>
          <div className="row">
            <div className="col">
              <DashboardSeasonOverview {...props} setFilterStatus={setFilterStatus} seasonId={selectedSeason?.id} />

            </div>
          </div>

        </div>
      </div>
      <div className={classnames('row with-margin-top-extra', { 'd-none': !_.isEmpty(props.seasons.currentSeason) })}>
        <div className="col">
          <DashboardActivePaymentRequests {...props} filterStatus={filterStatus} />
        </div>
      </div>
    </div>
  );
};

export default connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
}))(DashboardAllTeams);
